/**
 * Sprint 4I1.B1.1 — Tabella Spese veicolo unificate, layout 2 righe inline.
 *
 * Patterns per ciascun tipo:
 *  - Miniatto sistema: 2 importi (esente + ivato) + 2 IVA + TOTALE readonly
 *  - Trasporto sistema: descrizione + importo + IVA + spesa interna flag
 *  - Generica/Ripristino utente: descrizione + importo + IVA + spesa interna + stato
 *  - Tutte: riga 2 amministrativa (anagrafica + n° fattura + data + note)
 *
 * NO popup MiniattoModal (rimosso). Tutto inline.
 */
import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import CodiceIvaPicker from "@/components/CodiceIvaPicker";
import OrigineIvaLabel from "@/components/OrigineIvaLabel";
import CurrencyInput, { formatCurrencyIT } from "@/components/CurrencyInput";
import AnagraficaPicker from "@/components/AnagraficaPicker";

const STATI_OPTS = [
  { value: "da_fare", label: "Da fare" },
  { value: "eseguito", label: "Eseguito" },
  { value: "pagato", label: "Pagato" },
];

const TIPO_LABEL = {
  miniatto: "Miniatto",
  trasporto: "Trasporto",
  generica: "Generica",
  ripristino: "Ripristino",
};

function tipoColor(tipo) {
  if (tipo === "miniatto") return "bg-amber-100 text-amber-800 border-amber-300";
  if (tipo === "trasporto") return "bg-sky-100 text-sky-800 border-sky-300";
  if (tipo === "generica") return "bg-slate-100 text-slate-700 border-slate-300";
  if (tipo === "ripristino") return "bg-rose-100 text-rose-800 border-rose-300";
  return "bg-slate-100 text-slate-700 border-slate-300";
}

function statoColor(stato) {
  if (stato === "pagato") return "bg-emerald-100 text-emerald-800 border-emerald-300";
  if (stato === "eseguito") return "bg-blue-100 text-blue-800 border-blue-300";
  return "bg-amber-100 text-amber-800 border-amber-300";
}

// Wrapper riusabile: input con sfondo giallo
const AMBER_WRAP = "rounded border-l-4 border-amber-400 bg-amber-50 px-1.5 py-0.5";


export default function SpeseVeicoloTable({ vehicleId, companyId, importi, onReload }) {
  const rows = importi?.spese_veicolo || [];
  const [saving, setSaving] = useState(false);
  // Sprint 4I1.C — Default IVA azienda per pre-fill voci utente nuove
  const [aziendaIvaDefault, setAziendaIvaDefault] = useState(null);
  useEffect(() => {
    if (!companyId) { setAziendaIvaDefault(null); return; }
    let m = true;
    api.get(`/companies/${companyId}`).then((r) => {
      if (m) setAziendaIvaDefault(r.data?.iva_codice_default || "I22");
    }).catch(() => { if (m) setAziendaIvaDefault("I22"); });
    return () => { m = false; };
  }, [companyId]);

  const tot_generiche = importi?.totale_spese_generiche_derived || 0;
  const tot_ripristino = importi?.totale_spese_ripristino_derived || 0;
  // Sprint 4I1.B1.2.1: TOTALE SPESE VEICOLO esclude le righe sistema
  // (miniatto/trasporto sono già conteggiati nel mini-totale APPRONTAMENTO).
  // Backend `totale_spese_veicolo` include sistema (è la fonte di
  // `totale_costo_auto`), quindi qui ricalcoliamo solo utente.
  const tot_totale = tot_generiche + tot_ripristino;
  const tot_da_fare = importi?.totale_da_fare || 0;
  const tot_eseguito = importi?.totale_eseguito || 0;
  const tot_pagato = importi?.totale_pagato || 0;

  const updateRow = async (spesaId, patch) => {
    setSaving(true);
    try {
      await api.patch(`/vehicles/${vehicleId}/spese_veicolo/${spesaId}`, patch);
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento");
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (row) => {
    if (row.is_sistema) {
      toast.error("Riga di sistema non eliminabile");
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Eliminare la riga "${row.descrizione || TIPO_LABEL[row.tipo]}"?`)) return;
    setSaving(true);
    try {
      await api.delete(`/vehicles/${vehicleId}/spese_veicolo/${row.id}`);
      onReload && onReload();
      toast.success("Riga eliminata");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    } finally {
      setSaving(false);
    }
  };

  const addRow = async (tipo) => {
    setSaving(true);
    try {
      // Sprint 4I1.C — pre-fill IVA con default azienda (non hardcoded)
      const defIva = aziendaIvaDefault || "I22";
      await api.post(`/vehicles/${vehicleId}/spese_veicolo`, {
        tipo, descrizione: "", importo: 0, iva_codice: defIva, stato: "da_fare",
      });
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione");
    } finally {
      setSaving(false);
    }
  };

  // Sprint 4I1.B1.2 — split righe sistema (Approntamento) vs utente (Spese veicolo)
  const sistemaRows = rows.filter((r) => r.is_sistema);
  const utenteRows = rows.filter((r) => !r.is_sistema);

  // Mini-totale Approntamento = miniatto (ivato+esente) + trasporto.importo
  const miniattoRow = sistemaRows.find((r) => r.tipo === "miniatto");
  const trasportoRow = sistemaRows.find((r) => r.tipo === "trasporto");
  const tot_approntamento = (
    Number(miniattoRow?.importo_ivato || 0)
    + Number(miniattoRow?.importo_esente || 0)
    + Number(trasportoRow?.importo || 0)
  );

  return (
    <div className="space-y-4">
      {/* ============================================================
          RIQUADRO 1 — APPRONTAMENTO (righe sistema: Miniatto + Trasporto)
          ============================================================ */}
      <Card className="p-4" data-testid="approntamento-section">
        <div className="flex items-center justify-between mb-3 pb-2 border-b">
          <h3 className="font-heading text-sm uppercase text-slate-500 tracking-wide">
            Approntamento
          </h3>
          <div className="text-sm">
            <span className="text-slate-500 mr-2">Tot. approntamento</span>
            <span className="font-mono font-semibold text-amber-700"
                  data-testid="approntamento-tot-totale">
              {formatCurrencyIT(tot_approntamento)}
            </span>
          </div>
        </div>
        <div className="space-y-3" data-testid="approntamento-list">
          {sistemaRows.map((row) => (
            <SpesaRow
              key={row.id}
              row={row}
              saving={saving}
              companyId={companyId}
              onChange={(patch) => updateRow(row.id, patch)}
              onDelete={() => deleteRow(row)}
            />
          ))}
        </div>
      </Card>

      {/* ============================================================
          RIQUADRO 2 — SPESE VEICOLO (righe utente: Generica + Ripristino)
          ============================================================ */}
      <Card className="p-4" data-testid="spese-veicolo-section">
        <div className="flex items-center justify-between mb-3 pb-2 border-b">
          <h3 className="font-heading text-sm uppercase text-slate-500 tracking-wide">
            Spese veicolo
          </h3>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => addRow("generica")}
                    disabled={saving} data-testid="spese-add-generica">
              <Plus className="h-3.5 w-3.5 mr-1" /> Generica
            </Button>
            <Button size="sm" variant="outline" onClick={() => addRow("ripristino")}
                    disabled={saving} data-testid="spese-add-ripristino">
              <Plus className="h-3.5 w-3.5 mr-1" /> Ripristino
            </Button>
          </div>
        </div>

        <div className="space-y-3" data-testid="spese-veicolo-list">
          {utenteRows.map((row) => (
            <SpesaRow
              key={row.id}
              row={row}
              saving={saving}
              aziendaIvaDefault={aziendaIvaDefault}
              companyId={companyId}
              onChange={(patch) => updateRow(row.id, patch)}
              onDelete={() => deleteRow(row)}
            />
          ))}
          {utenteRows.length === 0 && (
            <div className="text-center py-6 text-slate-400 italic">
              Nessuna spesa registrata.
            </div>
          )}
        </div>

        {/* Footer: totali + pill stato */}
        <div className="mt-4 pt-3 border-t grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Tot. spese generiche</span>
              <span className="font-mono" data-testid="spese-tot-generiche">
                {formatCurrencyIT(tot_generiche)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Tot. spese ripristino</span>
              <span className="font-mono" data-testid="spese-tot-ripristino">
                {formatCurrencyIT(tot_ripristino)}
              </span>
            </div>
            <div className="flex justify-between pt-1 border-t font-semibold">
              <span>TOTALE SPESE VEICOLO</span>
              <span className="font-mono text-amber-700" data-testid="spese-tot-totale">
                {formatCurrencyIT(tot_totale)}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-start justify-end gap-2">
            <span className={`px-3 py-1.5 rounded-md border text-xs font-medium ${statoColor("da_fare")}`}
                  data-testid="spese-pill-da-fare">
              Da fare: <span className="font-mono ml-1">{formatCurrencyIT(tot_da_fare)}</span>
            </span>
            <span className={`px-3 py-1.5 rounded-md border text-xs font-medium ${statoColor("eseguito")}`}
                  data-testid="spese-pill-eseguito">
              Eseguito: <span className="font-mono ml-1">{formatCurrencyIT(tot_eseguito)}</span>
            </span>
            <span className={`px-3 py-1.5 rounded-md border text-xs font-medium ${statoColor("pagato")}`}
                  data-testid="spese-pill-pagato">
              Pagato: <span className="font-mono ml-1">{formatCurrencyIT(tot_pagato)}</span>
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
}


function SpesaRow({ row, saving, onChange, onDelete, aziendaIvaDefault, companyId }) {
  const isSistema = !!row.is_sistema;
  const isMiniatto = row.tipo === "miniatto";
  const isTrasporto = row.tipo === "trasporto";

  const [local, setLocal] = useState({
    descrizione: row.descrizione || "",
    importo: row.importo || 0,
    importo_ivato: row.importo_ivato ?? 0,
    importo_esente: row.importo_esente ?? 0,
    iva_codice: row.iva_codice || null,
    iva_codice_ivato: row.iva_codice_ivato || (isMiniatto ? "I22" : null),
    iva_codice_esente: row.iva_codice_esente || (isMiniatto ? "N1" : null),
    stato: row.stato || "da_fare",
    fornitore_id: row.fornitore_id || null,
    spesa_interna: !!row.spesa_interna,
    fattura_numero: row.fattura_numero || "",
    fattura_data: row.fattura_data || "",
    note: row.note || "",
  });
  // Sprint 4I1.C / C.1 — tracking origine IVA (solo righe utente).
  // Init "null" (sconosciuto): la label non si mostra finché non c'è
  // interazione esplicita o non si riconosce la propagazione da fornitore.
  const [ivaSource, setIvaSource] = useState(null);
  const [fornitoreNome, setFornitoreNome] = useState("");

  // Sprint 4I1.C.1 — Quando arriva aziendaIvaDefault dal fetch async del
  // parent, ri-valuta lo stato della label: se l'IVA della riga coincide
  // col default azienda e nessuna interazione utente è avvenuta, label="azienda".
  useEffect(() => {
    if (isSistema) return;
    if (ivaSource !== null) return;
    if (!aziendaIvaDefault) return;
    if (row.iva_codice && row.iva_codice === aziendaIvaDefault) {
      setIvaSource("azienda");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aziendaIvaDefault, isSistema, row.iva_codice]);

  // Sync from prop (server recompute)
  useEffect(() => {
    setLocal({
      descrizione: row.descrizione || "",
      importo: row.importo || 0,
      importo_ivato: row.importo_ivato ?? 0,
      importo_esente: row.importo_esente ?? 0,
      iva_codice: row.iva_codice || null,
      iva_codice_ivato: row.iva_codice_ivato || (isMiniatto ? "I22" : null),
      iva_codice_esente: row.iva_codice_esente || (isMiniatto ? "N1" : null),
      stato: row.stato || "da_fare",
      fornitore_id: row.fornitore_id || null,
      spesa_interna: !!row.spesa_interna,
      fattura_numero: row.fattura_numero || "",
      fattura_data: row.fattura_data || "",
      note: row.note || "",
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id, row.descrizione, row.importo, row.importo_ivato, row.importo_esente,
      row.iva_codice, row.iva_codice_ivato, row.iva_codice_esente, row.stato,
      row.fornitore_id, row.spesa_interna, row.fattura_numero, row.fattura_data, row.note]);

  const setL = (patch) => setLocal((l) => ({ ...l, ...patch }));

  const patchField = (field, value) => {
    if (row[field] !== value) onChange({ [field]: value });
  };

  // Sprint 4I1.C — fetch nome fornitore + propaga IVA su righe utente non-override
  useEffect(() => {
    if (isSistema) return;
    if (!local.fornitore_id) { setFornitoreNome(""); return; }
    let cancelled = false;
    api.get(`/anagrafiche/${local.fornitore_id}`).then((r) => {
      if (cancelled) return;
      const a = r.data || {};
      const nameLabel = a.ragione_sociale
        || `${a.cognome || ""} ${a.nome || ""}`.trim()
        || a.codice || "Fornitore";
      setFornitoreNome(nameLabel);
      const ivaCod = a.iva_codice || null;
      if (ivaCod && ivaSource !== "override" && ivaCod !== local.iva_codice) {
        setL({ iva_codice: ivaCod });
        onChange({ iva_codice: ivaCod });
        setIvaSource("fornitore");
      } else if (ivaCod && ivaCod === local.iva_codice && ivaSource !== "override") {
        setIvaSource("fornitore");
      }
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local.fornitore_id, isSistema]);

  const miniattoTot = (Number(local.importo_ivato) || 0) + (Number(local.importo_esente) || 0);

  return (
    <div className={`border rounded-md p-3 space-y-2 ${isSistema ? "bg-slate-50/50 border-slate-300" : "bg-white"}`}
         data-testid={`spesa-row-${row.id}`}>
      {/* Riga 1 — campi operativi */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Categoria badge — gap ridotto verso descrizione */}
        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px] font-medium shrink-0 ${tipoColor(row.tipo)}`}
             style={{ minWidth: "94px" }}>
          {isSistema && <Lock className="h-3 w-3" />}
          {TIPO_LABEL[row.tipo]}
        </div>

        {isMiniatto ? (
          // === MINIATTO: 2 importi + 2 IVA + totale readonly ===
          <>
            <div className="flex items-center gap-1">
              <span className="text-[10px] uppercase text-slate-500">Esente</span>
              <div className={AMBER_WRAP} style={{ width: "110px" }}>
                <CurrencyInput
                  value={local.importo_esente}
                  onChange={(v) => {
                    // CurrencyInput.onChange parte 1x al blur col valore numerico finale.
                    // Aggiorna local state E dispara la PATCH dedicata se cambia il valore.
                    setL({ importo_esente: v });
                    const newVal = v ?? 0;
                    if ((row.importo_esente ?? 0) !== newVal) onChange({ importo_esente: newVal });
                  }}
                  data-testid={`spesa-mini-esente-${row.id}`}
                />
              </div>
              <div style={{ width: "100px" }}>
                <CodiceIvaPicker value={local.iva_codice_esente}
                                  onChange={(v) => { setL({ iva_codice_esente: v }); onChange({ iva_codice_esente: v }); }}
                                  compact testId={`spesa-mini-iva-esente-${row.id}`} />
              </div>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[10px] uppercase text-slate-500">Ivato</span>
              <div className={AMBER_WRAP} style={{ width: "110px" }}>
                <CurrencyInput
                  value={local.importo_ivato}
                  onChange={(v) => {
                    setL({ importo_ivato: v });
                    const newVal = v ?? 0;
                    if ((row.importo_ivato ?? 0) !== newVal) onChange({ importo_ivato: newVal });
                  }}
                  data-testid={`spesa-mini-ivato-${row.id}`}
                />
              </div>
              <div style={{ width: "100px" }}>
                <CodiceIvaPicker value={local.iva_codice_ivato}
                                  onChange={(v) => { setL({ iva_codice_ivato: v }); onChange({ iva_codice_ivato: v }); }}
                                  compact testId={`spesa-mini-iva-ivato-${row.id}`} />
              </div>
            </div>
            <div className="ml-auto flex items-center gap-1">
              <span className="text-[10px] uppercase text-slate-500">Totale</span>
              <span className="font-mono font-semibold text-amber-700 text-sm"
                    data-testid={`spesa-mini-totale-${row.id}`}>
                {formatCurrencyIT(miniattoTot)}
              </span>
            </div>
          </>
        ) : (
          // === TRASPORTO / GENERICA / RIPRISTINO ===
          <>
            <Input value={local.descrizione}
                   onChange={(e) => setL({ descrizione: e.target.value })}
                   onBlur={() => patchField("descrizione", local.descrizione)}
                   placeholder="Descrizione"
                   className="h-8 text-sm flex-1 min-w-[200px]"
                   data-testid={`spesa-desc-${row.id}`} />
            <div className={AMBER_WRAP} style={{ width: "120px" }}>
              <CurrencyInput value={local.importo}
                              onChange={(v) => {
                                setL({ importo: v });
                                const newVal = v ?? 0;
                                if ((row.importo ?? 0) !== newVal) onChange({ importo: newVal });
                              }}
                              data-testid={`spesa-importo-${row.id}`} />
            </div>
            <div style={{ width: "120px" }}>
              <CodiceIvaPicker value={local.iva_codice}
                                onChange={(v) => { setL({ iva_codice: v }); onChange({ iva_codice: v }); if (!isSistema) setIvaSource("override"); }}
                                compact testId={`spesa-iva-${row.id}`} />
              {!isSistema && (
                <OrigineIvaLabel source={ivaSource} fornitoreNome={fornitoreNome}
                                 testId={`spesa-iva-origine-${row.id}`} />
              )}
            </div>
            <label className="flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer shrink-0">
              <input type="checkbox" checked={local.spesa_interna}
                     onChange={(e) => { setL({ spesa_interna: e.target.checked }); onChange({ spesa_interna: e.target.checked }); }}
                     data-testid={`spesa-interna-${row.id}`} />
              Spesa interna
            </label>
            {!isTrasporto && (
              <div style={{ width: "110px" }}>
                <Select value={local.stato}
                        onValueChange={(v) => { setL({ stato: v }); onChange({ stato: v }); }}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`spesa-stato-${row.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATI_OPTS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </>
        )}
      </div>

      {/* Riga 2 — campi amministrativi (anagrafica + fattura + data + note) */}
      <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-dashed border-slate-200">
        <div className="flex-1 min-w-[220px]">
          <AnagraficaPicker value={local.fornitore_id}
                             onChange={(v) => { setL({ fornitore_id: v }); onChange({ fornitore_id: v }); }}
                             filterCategoria="fornitore_servizi_veicoli"
                             contextCompanyId={companyId || null}
                             testIdPrefix={`spesa-fornitore-${row.id}`} />
        </div>
        <Input value={local.fattura_numero}
               onChange={(e) => setL({ fattura_numero: e.target.value })}
               onBlur={() => patchField("fattura_numero", local.fattura_numero || null)}
               placeholder="N° fattura"
               className="h-8 text-sm"
               style={{ width: "130px" }}
               data-testid={`spesa-fattnum-${row.id}`} />
        <Input type="date" value={local.fattura_data || ""}
               onChange={(e) => setL({ fattura_data: e.target.value })}
               onBlur={() => patchField("fattura_data", local.fattura_data || null)}
               className="h-8 text-sm"
               style={{ width: "150px" }}
               data-testid={`spesa-fattdata-${row.id}`} />
        <Input value={local.note}
               onChange={(e) => setL({ note: e.target.value })}
               onBlur={() => patchField("note", local.note)}
               placeholder="Note"
               className="h-8 text-sm flex-1 min-w-[150px]"
               data-testid={`spesa-note-${row.id}`} />
        {!isSistema ? (
          <button onClick={onDelete}
                  disabled={saving}
                  className="p-1.5 rounded text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                  title="Elimina riga"
                  data-testid={`spesa-delete-${row.id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        ) : (
          <button disabled
                  className="p-1.5 rounded text-slate-300 cursor-not-allowed"
                  title="Riga di sistema, non eliminabile"
                  data-testid={`spesa-delete-${row.id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
